import React, { useState, useCallback } from 'react';
import { 
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ScaleIcon,
  BriefcaseIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

// Widget types
export type WidgetType = 'task_list' | 'case_status' | 'document_summary' | 'workflow_progress';

// Base widget interface
export interface BaseWidget {
  id: string;
  type: WidgetType;
  title: string;
  subtitle?: string;
  expanded?: boolean;
  timestamp?: Date;
}

// Task list widget
export interface TaskItem {
  id: string;
  title: string;
  completed: boolean;
  priority?: 'low' | 'medium' | 'high';
  dueDate?: Date;
  assignee?: string;
}

export interface TaskListWidget extends BaseWidget {
  type: 'task_list';
  tasks: TaskItem[];
  completedCount: number;
  totalCount: number;
}

// Case status widget
export interface CaseStatusWidget extends BaseWidget {
  type: 'case_status';
  matterName: string;
  currentPhase: string;
  nextDeadline?: {
    date: Date;
    description: string;
  };
  recentActivity: Array<{
    id: string;
    description: string;
    timestamp: Date;
    type: 'update' | 'deadline' | 'document' | 'communication';
  }>;
}

// Document summary widget
export interface DocumentSummaryWidget extends BaseWidget {
  type: 'document_summary';
  documentName: string;
  pageCount: number;
  wordCount: number;
  keyPoints: string[];
  legalCitations: string[];
  lastModified: Date;
}

// Workflow progress widget
export interface WorkflowProgressWidget extends BaseWidget {
  type: 'workflow_progress';
  workflowName: string;
  currentStep: number;
  totalSteps: number;
  completedSteps: Array<{
    id: string;
    name: string;
    completedAt: Date;
  }>;
  currentStepName: string;
  status: 'running' | 'paused' | 'completed' | 'failed';
}

export type Widget = TaskListWidget | CaseStatusWidget | DocumentSummaryWidget | WorkflowProgressWidget;

interface InlineWorkflowWidgetProps {
  widget: Widget;
  onTaskToggle?: (taskId: string, completed: boolean) => void;
  onWorkflowAction?: (action: 'resume' | 'pause' | 'retry') => void;
  onExpand?: (widgetId: string, expanded: boolean) => void;
  compact?: boolean;
}

const InlineWorkflowWidget: React.FC<InlineWorkflowWidgetProps> = ({
  widget,
  onTaskToggle,
  onWorkflowAction,
  onExpand,
  compact = false
}) => {
  const [isExpanded, setIsExpanded] = useState(widget.expanded || false);

  const toggleExpanded = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpand?.(widget.id, newExpanded);
  }, [isExpanded, onExpand, widget.id]);

  const handleTaskToggle = useCallback((taskId: string, completed: boolean) => {
    onTaskToggle?.(taskId, completed);
  }, [onTaskToggle]);

  const handleWorkflowAction = useCallback((action: 'resume' | 'pause' | 'retry') => {
    onWorkflowAction?.(action);
  }, [onWorkflowAction]);

  // Render task list widget
  const renderTaskList = (taskWidget: TaskListWidget) => (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {taskWidget.completedCount}/{taskWidget.totalCount} Complete
          </span>
        </div>
        <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(taskWidget.completedCount / taskWidget.totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Task list */}
      {isExpanded && (
        <div className="space-y-2">
          {taskWidget.tasks.map((task) => (
            <div 
              key={task.id} 
              className={`
                flex items-center gap-3 p-2 rounded-lg border 
                ${task.completed ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}
                transition-all duration-200
              `}
            >
              <button
                onClick={() => handleTaskToggle(task.id, !task.completed)}
                className="flex-shrink-0"
              >
                {task.completed ? (
                  <CheckCircleIconSolid className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 rounded-full hover:border-green-500 transition-colors" />
                )}
              </button>
              
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${task.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {task.title}
                </div>
                {task.dueDate && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Due: {task.dueDate.toLocaleDateString()}
                  </div>
                )}
              </div>

              {task.priority && (
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${task.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : 
                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' : 
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}
                `}>
                  {task.priority}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render case status widget
  const renderCaseStatus = (caseWidget: CaseStatusWidget) => (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3">
        <BriefcaseIcon className="w-5 h-5 text-lex-accent mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {caseWidget.matterName}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Status: {caseWidget.currentPhase}
          </div>
        </div>
      </div>

      {/* Next deadline */}
      {caseWidget.nextDeadline && (
        <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <ClockIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-900 dark:text-amber-100">
              {caseWidget.nextDeadline.description}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-300">
              {caseWidget.nextDeadline.date.toLocaleDateString()}
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      {isExpanded && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Recent Activity</div>
          {caseWidget.recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className={`
                w-2 h-2 rounded-full mt-2 flex-shrink-0
                ${activity.type === 'update' ? 'bg-lex-accent' :
                  activity.type === 'deadline' ? 'bg-red-500' : 
                  activity.type === 'document' ? 'bg-green-500' : 'bg-purple-500'}
              `} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {activity.description}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {activity.timestamp.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render document summary widget
  const renderDocumentSummary = (docWidget: DocumentSummaryWidget) => (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3">
        <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {docWidget.documentName}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {docWidget.pageCount} pages • {docWidget.wordCount.toLocaleString()} words
          </div>
        </div>
      </div>

      {/* Key points */}
      {isExpanded && (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Key Points</div>
            <div className="space-y-1">
              {docWidget.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Legal citations */}
          {docWidget.legalCitations.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Legal Citations</div>
              <div className="space-y-1">
                {docWidget.legalCitations.map((citation, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <ScaleIcon className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                    <span className="text-gray-700 dark:text-gray-300 font-mono text-xs">{citation}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render workflow progress widget
  const renderWorkflowProgress = (workflowWidget: WorkflowProgressWidget) => (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`
            w-3 h-3 rounded-full
            ${workflowWidget.status === 'running' ? 'bg-lex-accent' :
              workflowWidget.status === 'paused' ? 'bg-yellow-500' : 
              workflowWidget.status === 'completed' ? 'bg-green-500' : 'bg-red-500'}
          `} />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {workflowWidget.workflowName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {workflowWidget.currentStep}/{workflowWidget.totalSteps}
          </span>
          {workflowWidget.status === 'running' && (
            <button
              onClick={() => handleWorkflowAction('pause')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <PauseIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          {workflowWidget.status === 'paused' && (
            <button
              onClick={() => handleWorkflowAction('resume')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <PlayIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          {workflowWidget.status === 'failed' && (
            <button
              onClick={() => handleWorkflowAction('retry')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <ExclamationTriangleIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className={`
            h-2 rounded-full transition-all duration-300
            ${workflowWidget.status === 'completed' ? 'bg-green-500' : 
              workflowWidget.status === 'failed' ? 'bg-red-500' : 'bg-lex-accent'}
          `}
          style={{ width: `${(workflowWidget.currentStep / workflowWidget.totalSteps) * 100}%` }}
        />
      </div>

      {/* Current step */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Current: {workflowWidget.currentStepName}
      </div>

      {/* Completed steps */}
      {isExpanded && workflowWidget.completedSteps.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Completed Steps</div>
          {workflowWidget.completedSteps.map((step) => (
            <div key={step.id} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <CheckCircleIconSolid className="w-4 h-4 text-green-600 dark:text-green-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 dark:text-gray-100">{step.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {step.completedAt.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const cardPadding = compact ? 'p-3' : 'p-4';
  const cardSpacing = compact ? 'space-y-2' : 'space-y-3';

  return (
    <div className={`
      border border-gray-200 dark:border-gray-700 rounded-lg 
      bg-white dark:bg-gray-800 shadow-sm hover:shadow-md 
      transition-all duration-200 ${cardPadding}
    `}>
      {/* Widget header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            {widget.title}
          </h3>
          {widget.subtitle && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {widget.subtitle}
            </span>
          )}
        </div>
        <button
          onClick={toggleExpanded}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Widget content */}
      <div className={cardSpacing}>
        {widget.type === 'task_list' && renderTaskList(widget as TaskListWidget)}
        {widget.type === 'case_status' && renderCaseStatus(widget as CaseStatusWidget)}
        {widget.type === 'document_summary' && renderDocumentSummary(widget as DocumentSummaryWidget)}
        {widget.type === 'workflow_progress' && renderWorkflowProgress(widget as WorkflowProgressWidget)}
      </div>
    </div>
  );
};

export default InlineWorkflowWidget; 